import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Building2, Globe2, Loader2, Search, Users2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import FeedbackModal from "@/components/FeedbackModal";
import { useApp } from "@/contexts/AppContext";
import { sendInvitation } from "@/api/auth/auth";
import { getErrorMessage } from '@/utils/errorUtils';

type AdminRecord = Record<string, unknown>;

type PaginationState = {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  hasNext: boolean;
};

type Props = {
  entityLabel: string;
  totalLabel?: string;
  requestLabel: string;
  requestDescription: string;
  searchPlaceholder: string;
  inviteRole: string;
  entityDescription?: string;
  ownerColumnLabel?: string;
  dateColumnLabel?: string;
  emptyEntitiesLabel?: string;
  emptyRequestsLabel?: string;
  enableEntityFilters?: boolean;
  countryPlaceholder?: string;
  listEntities: (params: Record<string, unknown>) => Promise<unknown>;
  listRequests: (params: Record<string, unknown>) => Promise<unknown>;
  rejectEntity: (id: string) => Promise<unknown>;
  rejectEntityByRequestId?: boolean;
  approveRequest?: (id: string) => Promise<unknown>;
  themeKey?: "organisation" | "countryManager";
  entityIcon?: "building" | "globe";
};

const PAGE_SIZE = 10;

const pick = (record: AdminRecord, keys: string[]) => {
  for (const key of keys) {
    const value = key.split(".").reduce<unknown>((acc, part) => acc?.[part], record);
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return "";
};

const getList = (response: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = key.split(".").reduce<unknown>((acc, part) => acc?.[part], response);
    if (Array.isArray(value)) return value;
  }
  return [];
};

const getPagination = (response: Record<string, unknown>, page: number, total: number): PaginationState => {
  const pagination = ((response as unknown as Record<string, unknown>)?.data as unknown as Record<string, unknown>)?.pagination || (response as unknown as Record<string, unknown>)?.pagination || {};
  const p = pagination as Record<string, unknown>;
  const currentPage = (p.currentPage || p.current_page || p.page || page) as number;
  const totalPages = (p.totalPages || p.total_pages || 1) as number;
  const totalItems =
    (p.totalItems || p.total_items || p.total || p.count || total) as number;
  return {
    currentPage,
    totalPages,
    totalItems,
    hasNext: (p.hasNext as boolean) ?? (p.has_next as boolean) ?? currentPage < totalPages,
  };
};

const formatDate = (value?: string) => {
  if (!value) return "N/A";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "N/A" : parsed.toLocaleString();
};

export default function RoleManagementPage({
  entityLabel,
  totalLabel,
  requestLabel,
  requestDescription,
  searchPlaceholder,
  inviteRole,
  entityDescription,
  ownerColumnLabel,
  dateColumnLabel,
  emptyEntitiesLabel,
  emptyRequestsLabel,
  enableEntityFilters,
  countryPlaceholder,
  listEntities,
  listRequests,
  rejectEntity,
  rejectEntityByRequestId = false,
  approveRequest,
  themeKey: _themeKey,
  entityIcon = "building",
}: Props) {
  const { t } = useTranslation();
  const { theme } = useApp();
  const isDark = theme === "dark";
  const EntityIcon = entityIcon === "globe" ? Globe2 : Building2;
  const entityPlural = entityLabel.endsWith("s") ? entityLabel : `${entityLabel}s`;

  const [entities, setEntities] = useState<AdminRecord[]>([]);
  const [requests, setRequests] = useState<AdminRecord[]>([]);
  const [search, setSearch] = useState("");
  const [entityStatus, setEntityStatus] = useState("");
  const [entityCountry, setEntityCountry] = useState("");
  const [entityPage, setEntityPage] = useState(1);
  const [requestPage, setRequestPage] = useState(1);
  const [requestStatus, setRequestStatus] = useState("");
  const [entitiesLoading, setEntitiesLoading] = useState(false);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [loadingMoreEntities, setLoadingMoreEntities] = useState(false);
  const [loadingMoreRequests, setLoadingMoreRequests] = useState(false);
  const [invitingId, setInvitingId] = useState<string | null>(null);
  const [approvingRequestId, setApprovingRequestId] = useState<string | null>(null);
  const [rejectingEntityId, setRejectingEntityId] = useState<string | null>(null);
  const [sentInvites, setSentInvites] = useState<string[]>([]);
  const [error, setError] = useState("");
  const [feedback, setFeedback] = useState({
    isOpen: false,
    type: "success" as "success" | "error",
    title: "",
    message: "",
  });
  const [entityPagination, setEntityPagination] = useState<PaginationState>({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNext: false,
  });
  const [requestPagination, setRequestPagination] = useState<PaginationState>({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNext: false,
  });

  const getEntityId = (record: AdminRecord) =>
    String(pick(record, ["countryManagerId", "organizationId", "id", "_id", "userId"]) || "");
  const getRequestId = (record: AdminRecord) =>
    String(
      pick(record, [
        "countryManagerRequestId",
        "organizationRequestId",
        "organisationRequestId",
        "requestId",
        "id",
        "_id",
      ]) || ""
    );
  const getEmail = (record: AdminRecord) =>
    String(
      pick(record, [
        "email",
        "ownerEmail",
        "countryManagerEmail",
        "organizationEmail",
        "user.email",
        "operator.email",
      ]) || ""
    );
  const getStatus = (record: AdminRecord) =>
    String(
      pick(record, [
        "status",
        "requestStatus",
        "countryManagerRequestStatus",
        "organizationRequestStatus",
        "organisationRequestStatus",
      ]) || "pending"
    ).toLowerCase();
  const getName = (record: AdminRecord) => {
    const direct = pick(record, [
      "countryManagerName",
      "organizationName",
      "organisationName",
      "user.fullName",
      "countryManager.name",
      "organization.name",
      "name",
    ]);
    if (direct) return String(direct);
    const joined = `${record.firstName || ""} ${record.lastName || ""}`.trim();
    return joined || getEmail(record) || "Unknown";
  };
  const getOwner = (record: AdminRecord) =>
    String(record.ownerName || record.primaryCountry || record.country || record.countryName || "N/A");
  const getBecameAt = (record: AdminRecord) =>
    String(pick(record, ["becameCountryManagerAt", "becameOrganizationAt", "becameOrganisationAt", "approvedAt", "createdAt"]) || "");

  useEffect(() => {
    let active = true;
    const load = async () => {
      const loadMore = entityPage > 1;
      try {
        loadMore ? setLoadingMoreEntities(true) : setEntitiesLoading(true);
        setError("");
        const searchValue = search.trim() || undefined;
        const response = await listEntities({
          q: searchValue,
          search: searchValue,
          status: entityStatus || undefined,
          country: entityCountry.trim() || undefined,
          page: entityPage,
          limit: PAGE_SIZE,
        });
        if (!active) return;
        const next = getList(response as unknown as Record<string, unknown>, [
          "data.countryManagers",
          "data.country_managers",
          "data.organizations",
          "data.organisations",
          "data.items",
          "data.records",
          "countryManagers",
          "country_managers",
          "organizations",
          "organisations",
          "items",
          "records",
        ]);
        setEntities((prev) => (loadMore ? [...prev, ...next] : next));
        setEntityPagination(getPagination(response as unknown as Record<string, unknown>, entityPage, next.length));
      } catch (err: unknown) {
        if (!active) return;
        setError(String((err as unknown as Record<string, unknown>)?.message || `Failed to load ${entityPlural.toLowerCase()}.`));
        if (!loadMore) setEntities([]);
      } finally {
        if (!active) return;
        loadMore ? setLoadingMoreEntities(false) : setEntitiesLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [entityCountry, entityPage, entityPlural, entityStatus, listEntities, search]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      const loadMore = requestPage > 1;
      try {
        loadMore ? setLoadingMoreRequests(true) : setRequestsLoading(true);
        const response = await listRequests({
          search: search.trim() || undefined,
          status: requestStatus || undefined,
          country: entityCountry.trim() || undefined,
          page: requestPage,
          limit: PAGE_SIZE,
        });
        if (!active) return;
        const next = getList(response as unknown as Record<string, unknown>, [
          "data.countryManagerRequests",
          "data.country_manager_requests",
          "data.organizationRequests",
          "data.organisationRequests",
          "data.requests",
          "data.items",
          "data.records",
          "countryManagerRequests",
          "country_manager_requests",
          "organizationRequests",
          "organisationsRequests",
          "requests",
          "items",
          "records",
        ]);
        setRequests((prev) => (loadMore ? [...prev, ...next] : next));
        setRequestPagination(getPagination(response as unknown as Record<string, unknown>, requestPage, next.length));
      } catch (err: unknown) {
        if (!active) return;
        setError(String((err as unknown as Record<string, unknown>)?.message || `Failed to load ${requestLabel.toLowerCase()}.`));
        if (!loadMore) setRequests([]);
      } finally {
        if (!active) return;
        loadMore ? setLoadingMoreRequests(false) : setRequestsLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [entityCountry, listRequests, requestLabel, requestPage, requestStatus, search]);

  const filteredRequests = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return requests;
    return requests.filter((record) =>
      [getName(record), getEmail(record), getStatus(record), record.country, record.location]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(term)
    );
  }, [requests, search]);

  const openFeedback = (type: "success" | "error", title: string, message: string) =>
    setFeedback({ isOpen: true, type, title, message });

  const onInvite = async (record: AdminRecord) => {
    const email = getEmail(record);
    const requestId = getRequestId(record) || getEntityId(record);
    if (!email) {
      openFeedback("error", "Invite failed", "This request does not have an email address to invite.");
      return;
    }
    try {
      setInvitingId(requestId);
      const inviteRoles =
        inviteRole === "country-manager"
          ? ["country-manager", "country_manager", "countryManager"]
          : [inviteRole];
      let response: { message?: string } | null = null;
      let lastError: Error | null = null;

      for (const role of inviteRoles) {
        try {
          response = await sendInvitation({ email, role });
          break;
        } catch (err: unknown) {
          lastError = err as Error;
        }
      }

      if (!response) {
        throw lastError;
      }

      setSentInvites((prev) => (prev.includes(requestId) ? prev : [...prev, requestId]));
      openFeedback("success", "Invite sent", response.message || `The ${entityLabel.toLowerCase()} invitation has been sent successfully.`);
    } catch (err: unknown) {
      openFeedback("error", "Invite failed", String((err as unknown as Record<string, unknown>)?.message || `We could not send the ${entityLabel.toLowerCase()} invitation.`));
    } finally {
      setInvitingId(null);
    }
  };

  const onApproveRequest = async (record: AdminRecord) => {
    const requestId = getRequestId(record) || getEntityId(record);
    if (!requestId || !approveRequest) {
      openFeedback("error", "Approve failed", "This request does not have a valid request ID.");
      return;
    }

    try {
      setApprovingRequestId(requestId);
      const response = await approveRequest(requestId);
      setRequests((prev) =>
        prev.map((item) => (getRequestId(item) === requestId ? { ...item, status: "approved", requestStatus: "approved" } : item))
      );
      openFeedback("success", "Request approved", getErrorMessage(response) || `The ${entityLabel.toLowerCase()} request has been approved successfully.`);
    } catch (err: unknown) {
      openFeedback("error", "Approve failed", String((err as unknown as Record<string, unknown>)?.message || `We could not approve the ${entityLabel.toLowerCase()} request.`));
    } finally {
      setApprovingRequestId(null);
    }
  };

  const onRejectEntity = async (record: AdminRecord) => {
    const targetId = rejectEntityByRequestId
      ? String(
          pick(record, [
            "countryManagerRequestId",
            "organizationRequestId",
            "organisationRequestId",
            "requestId",
            "id",
            "_id",
            "userId",
          ]) || ""
        )
      : getEntityId(record);

    if (!targetId) {
      openFeedback(
        "error",
        "Reject failed",
        rejectEntityByRequestId
          ? `This ${entityLabel.toLowerCase()} does not have a valid request ID.`
          : `This ${entityLabel.toLowerCase()} does not have a valid ID.`
      );
      return;
    }
    try {
      setRejectingEntityId(targetId);
      console.log('[DEBUG REJECT] Full record:', JSON.stringify(record, null, 2));
      console.log('[DEBUG REJECT] targetId being sent:', targetId);
      console.log('[DEBUG REJECT] rejectEntityByRequestId:', rejectEntityByRequestId);
      const response = await rejectEntity(targetId);
      setEntities((prev) =>
        prev.filter((item) =>
          (rejectEntityByRequestId
            ? String(
                pick(item, [
                  "countryManagerRequestId",
                  "organizationRequestId",
                  "organisationRequestId",
                  "requestId",
                  "id",
                  "_id",
                  "userId",
                ]) || ""
              )
            : getEntityId(item)) !== targetId
        )
      );
      setEntityPagination((prev) => ({ ...prev, totalItems: Math.max(0, prev.totalItems - 1) }));
      openFeedback("success", `${entityLabel} rejected`, getErrorMessage(response) || `The ${entityLabel.toLowerCase()} has been rejected successfully.`);
    } catch (err: unknown) {
      openFeedback("error", "Reject failed", String((err as unknown as Record<string, unknown>)?.message || `We could not reject the ${entityLabel.toLowerCase()}.`));
    } finally {
      setRejectingEntityId(null);
    }
  };

  return (
    <>
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Card className="p-6 dark:bg-[#333436] dark:border-[#333436]">
            <h4 className="mb-2 text-sm text-foreground/60">{totalLabel || `Total ${entityPlural}`}</h4>
            <p className="text-3xl font-bold text-foreground">{entityPagination.totalItems}</p>
          </Card>
          <Card className="p-6 dark:bg-[#333436] dark:border-[#333436]">
            <h4 className="mb-2 text-sm text-foreground/60">{requestLabel}</h4>
            <p className="text-3xl font-bold text-foreground">{requestPagination.totalItems}</p>
          </Card>
        </div>

        <div className="relative">
          <Search className={`absolute left-4 top-1/2 -translate-y-1/2 ${isDark ? "text-gray-400" : "text-gray-500"}`} size={20} />
          <input
            type="text"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setEntityPage(1);
            }}
            placeholder={searchPlaceholder}
            className={`w-full rounded-lg border py-3 pl-12 pr-4 ${
              isDark ? "border-[#2b2b2c] bg-black text-white placeholder-gray-500" : "border-gray-300 bg-white text-gray-900 placeholder-gray-400"
            } focus:outline-none focus:ring-2 focus:ring-blue-500 transition`}
          />
        </div>

        {enableEntityFilters && (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto]">
            <input
              type="text"
              value={entityCountry}
              onChange={(event) => {
                setEntityCountry(event.target.value);
                setEntityPage(1);
              }}
              placeholder={countryPlaceholder || t('admin.roleManagement.countryPlaceholder', 'Filter by country code')}
              className={`w-full rounded-lg border px-4 py-3 ${
                isDark ? "border-[#2b2b2c] bg-black text-white placeholder-gray-500" : "border-gray-300 bg-white text-gray-900 placeholder-gray-400"
              } focus:outline-none focus:ring-2 focus:ring-blue-500 transition`}
            />

            <div className="flex flex-wrap gap-2">
              {[
                { label: t('admin.roleManagement.filter.all', 'All'), value: "" },
                { label: t('admin.roleManagement.filter.active', 'Active'), value: "active" },
                { label: t('admin.roleManagement.filter.inactive', 'Inactive'), value: "inactive" },
              ].map((filterOption) => (
                <button
                  key={filterOption.value || "all-status"}
                  type="button"
                  onClick={() => {
                    setEntityStatus(filterOption.value);
                    setEntityPage(1);
                  }}
                  className={`rounded-full px-4 py-2 text-sm font-medium transition ${
                    entityStatus === filterOption.value
                      ? "bg-blue-600 text-white"
                      : isDark
                        ? "bg-[#333436] text-gray-300 hover:bg-[#3d3f42]"
                        : "bg-white text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  {filterOption.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {error && (
          <div className={`rounded-lg border px-4 py-3 text-sm ${isDark ? "border-red-900 bg-red-950/40 text-red-300" : "border-red-200 bg-red-50 text-red-700"}`}>
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <Card className={`${isDark ? "border-[#2b2b2c] bg-[#333436]" : "border-gray-200 bg-white"} overflow-hidden border`}>
            <div className={`flex items-center gap-3 border-b px-6 py-4 ${isDark ? "border-[#2b2b2c]" : "border-gray-200"}`}>
              <EntityIcon className={isDark ? "text-blue-400" : "text-blue-700"} size={20} />
              <div>
                <h3 className={`font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>{entityPlural}</h3>
                <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>{entityDescription || `All ${entityPlural.toLowerCase()} fetched from the admin ${entityPlural.toLowerCase()} list.`}</p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${isDark ? "bg-black" : "bg-gray-50"} border-b ${isDark ? "border-[#2b2b2c]" : "border-gray-200"}`}>
                  <tr>
                    <th className={`px-6 py-4 text-left text-sm font-semibold ${isDark ? "text-gray-400" : "text-gray-600"}`}>{t('admin.roleManagement.table.name', 'Name')}</th>
                    <th className={`px-6 py-4 text-left text-sm font-semibold ${isDark ? "text-gray-400" : "text-gray-600"}`}>{t('admin.roleManagement.table.email', 'Email')}</th>
                    <th className={`px-6 py-4 text-left text-sm font-semibold ${isDark ? "text-gray-400" : "text-gray-600"}`}>{ownerColumnLabel || "Owner Name"}</th>
                    <th className={`px-6 py-4 text-left text-sm font-semibold ${isDark ? "text-gray-400" : "text-gray-600"}`}>{dateColumnLabel || `Became ${entityLabel} At`}</th>
                    <th className={`px-6 py-4 text-left text-sm font-semibold ${isDark ? "text-gray-400" : "text-gray-600"}`}>{t('admin.roleManagement.table.action', 'Action')}</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDark ? "divide-[#2b2b2c]" : "divide-gray-200"}`}>
                  {entitiesLoading && (
                    <tr>
                      <td colSpan={5} className="px-6 py-8 text-center">
                        <div className={`inline-flex items-center gap-2 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span>{t('admin.roleManagement.loading', 'Loading {{label}} data...', { label: entityLabel.toLowerCase() })}</span>
                        </div>
                      </td>
                    </tr>
                  )}
                  {!entitiesLoading && entities.map((record) => {
                    const entityId = getEntityId(record);
                    const entityRejectId = rejectEntityByRequestId
                      ? String(
                          pick(record, [
                            "countryManagerRequestId",
                            "organizationRequestId",
                            "organisationRequestId",
                            "requestId",
                            "id",
                            "_id",
                            "userId",
                          ]) || ""
                        )
                      : entityId;
                    const isRejecting = rejectingEntityId === entityRejectId;
                    return (
                      <tr key={entityId || getName(record)}>
                        <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>{getName(record)}</td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>{getEmail(record) || "N/A"}</td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>{getOwner(record)}</td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>{formatDate(getBecameAt(record))}</td>
                        <td className="px-6 py-4 text-sm">
                          <button
                            type="button"
                            onClick={() => onRejectEntity(record)}
                            disabled={!entityRejectId || isRejecting}
                            className={`inline-flex min-w-[92px] items-center justify-center rounded-lg px-4 py-2 text-sm font-medium transition ${
                              entityRejectId && !isRejecting
                                ? isDark ? "bg-red-600 text-white hover:bg-red-500" : "bg-red-600 text-white hover:bg-red-700"
                                : isDark ? "bg-[#1f2022] text-gray-500" : "bg-gray-100 text-gray-500"
                            } disabled:cursor-not-allowed`}
                          >
                            {isRejecting ? <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />{t('admin.roleManagement.button.rejecting', 'Rejecting...')}</span> : t('admin.roleManagement.button.reject', 'Reject')}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {!entitiesLoading && entities.length === 0 && (
                    <tr>
                      <td colSpan={5} className={`px-6 py-8 text-center text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>{emptyEntitiesLabel || `No ${entityPlural.toLowerCase()} found.`}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className={`flex items-center justify-between gap-4 border-t px-6 py-4 ${isDark ? "border-[#2b2b2c] bg-black" : "border-gray-200 bg-white"}`}>
              <div className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                {t('admin.roleManagement.pagination.resultsRange', {
                  defaultValue: '{{start}} to {{end}} of {{total}} results',
                  start: entityPagination.totalItems === 0 ? 0 : 1,
                  end: entityPagination.totalItems === 0 ? 0 : Math.min(entities.length, entityPagination.totalItems),
                  total: entityPagination.totalItems
                })}
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>{`${t('admin.roleManagement.pagination.page', 'Page')} ${entityPagination.currentPage} ${t('admin.roleManagement.pagination.of', 'of')} ${entityPagination.totalPages}`}</span>
                <button
                  onClick={() => setEntityPage((page) => page + 1)}
                  disabled={!entityPagination.hasNext}
                  className={`rounded-lg px-4 py-2 text-sm font-medium transition ${isDark ? "bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50" : "bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"}`}
                >
                  {loadingMoreEntities ? t('admin.roleManagement.button.loading', 'Loading...') : t('admin.roleManagement.button.loadMore', 'Load More')}
                </button>
              </div>
            </div>
          </Card>

          <Card className={`${isDark ? "border-[#2b2b2c] bg-[#333436]" : "border-gray-200 bg-white"} overflow-hidden border`}>
            <div className={`flex items-center gap-3 border-b px-6 py-4 ${isDark ? "border-[#2b2b2c]" : "border-gray-200"}`}>
              <Users2 className={isDark ? "text-blue-400" : "text-blue-700"} size={20} />
              <div>
                <h3 className={`font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>{requestLabel}</h3>
                <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>{requestDescription}</p>
              </div>
            </div>

            <div className={`flex flex-wrap gap-2 border-b px-6 py-4 ${isDark ? "border-[#2b2b2c] bg-black" : "border-gray-200 bg-gray-50"}`}>
              {[
                { label: t('admin.roleManagement.filter.all', 'All'), value: "" },
                { label: t('admin.roleManagement.filter.pending', 'Pending'), value: "pending" },
                { label: t('admin.roleManagement.filter.approved', 'Approved'), value: "approved" },
                { label: t('admin.roleManagement.filter.rejected', 'Rejected'), value: "rejected" },
              ].map((filterOption) => (
                <button
                  key={filterOption.value || "all"}
                  type="button"
                  onClick={() => {
                    setRequestStatus(filterOption.value);
                    setRequestPage(1);
                  }}
                  className={`rounded-full px-4 py-2 text-sm font-medium transition ${
                    requestStatus === filterOption.value ? "bg-blue-600 text-white" : isDark ? "bg-[#333436] text-gray-300 hover:bg-[#3d3f42]" : "bg-white text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  {filterOption.label}
                </button>
              ))}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${isDark ? "bg-black" : "bg-gray-50"} border-b ${isDark ? "border-[#2b2b2c]" : "border-gray-200"}`}>
                  <tr>
                    <th className={`px-6 py-4 text-left text-sm font-semibold ${isDark ? "text-gray-400" : "text-gray-600"}`}>{t('admin.roleManagement.table.name', 'Name')}</th>
                    <th className={`px-6 py-4 text-left text-sm font-semibold ${isDark ? "text-gray-400" : "text-gray-600"}`}>{t('admin.roleManagement.table.email', 'Email')}</th>
                    <th className={`px-6 py-4 text-left text-sm font-semibold ${isDark ? "text-gray-400" : "text-gray-600"}`}>{t('admin.roleManagement.table.status', 'Status')}</th>
                    <th className={`px-6 py-4 text-left text-sm font-semibold ${isDark ? "text-gray-400" : "text-gray-600"}`}>{t('admin.roleManagement.table.action', 'Action')}</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDark ? "divide-[#2b2b2c]" : "divide-gray-200"}`}>
                  {requestsLoading && (
                    <tr>
                      <td colSpan={4} className="px-6 py-8 text-center">
                        <div className={`inline-flex items-center gap-2 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span>{t('admin.roleManagement.loading', 'Loading {{label}} data...', { label: entityLabel.toLowerCase() })}</span>
                        </div>
                      </td>
                    </tr>
                  )}
                  {!requestsLoading && filteredRequests.map((record) => {
                    const requestId = getRequestId(record) || getEntityId(record);
                    const email = getEmail(record);
                    const status = getStatus(record);
                    const isInviting = invitingId === requestId;
                    const isApproving = approvingRequestId === requestId;
                    const canInvite = !approveRequest && status === "pending" && !!email;
                    const canApprove = !!approveRequest && !!requestId && status !== "approved";
                    let primaryActionLabel = approveRequest ? t('admin.roleManagement.button.approve', 'Approve') : t('admin.roleManagement.button.acceptInvite', 'Accept Invite');
                    if (!approveRequest) {
                      if (!email) primaryActionLabel = t('admin.roleManagement.button.noEmail', 'No Email');
                      else if (status === "approved") primaryActionLabel = t('admin.roleManagement.button.statusApproved', 'Approved');
                      else if (status === "rejected") primaryActionLabel = t('admin.roleManagement.button.statusRejected', 'Rejected');
                      else if (sentInvites.includes(requestId)) primaryActionLabel = t('admin.roleManagement.button.resendInvite', 'Resend Invite');
                    }
                    return (
                      <tr key={requestId || getName(record)}>
                        <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>{getName(record)}</td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>{email || "N/A"}</td>
                        <td className="px-6 py-4 text-sm">
                          <span className={`inline-flex rounded-md px-3 py-1 text-xs font-medium ${isDark ? "bg-blue-950/40 text-blue-300" : "bg-blue-100 text-blue-700"}`}>{t(`admin.roleManagement.status.${status}`, (status || "").charAt(0).toUpperCase() + (status || "").slice(1))}</span>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <div className="flex flex-wrap items-center gap-2">
                            <button
                              type="button"
                              onClick={() => (approveRequest ? onApproveRequest(record) : onInvite(record))}
                              disabled={(!canInvite && !canApprove) || isInviting || isApproving}
                              className={`inline-flex min-w-[120px] items-center justify-center rounded-lg px-4 py-2 text-sm font-medium transition ${
                                (canInvite || canApprove) && !isInviting && !isApproving
                                  ? isDark ? "bg-blue-600 text-white hover:bg-blue-500" : "bg-blue-600 text-white hover:bg-blue-700"
                                  : isDark ? "bg-[#1f2022] text-gray-500" : "bg-gray-100 text-gray-500"
                              } disabled:cursor-not-allowed`}
                            >
                              {isInviting ? (
                                <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />{t('admin.roleManagement.button.sending', 'Sending...')}</span>
                              ) : isApproving ? (
                                <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />{t('admin.roleManagement.button.approving', 'Approving...')}</span>
                              ) : (
                                primaryActionLabel
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {!requestsLoading && filteredRequests.length === 0 && (
                    <tr>
                      <td colSpan={4} className={`px-6 py-8 text-center text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>{emptyRequestsLabel || `No ${requestLabel.toLowerCase()} found.`}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className={`flex items-center justify-between gap-4 border-t px-6 py-4 ${isDark ? "border-[#2b2b2c] bg-black" : "border-gray-200 bg-white"}`}>
              <div className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                {t('admin.roleManagement.pagination.resultsRange', {
                  defaultValue: '{{start}} to {{end}} of {{total}} results',
                  start: filteredRequests.length === 0 ? 0 : 1,
                  end: Math.min(filteredRequests.length, requestPagination.totalItems),
                  total: requestPagination.totalItems
                })}
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>{`${t('admin.roleManagement.pagination.page', 'Page')} ${requestPagination.currentPage} ${t('admin.roleManagement.pagination.of', 'of')} ${requestPagination.totalPages}`}</span>
                <button
                  onClick={() => setRequestPage((page) => page + 1)}
                  disabled={!requestPagination.hasNext}
                  className={`rounded-lg px-4 py-2 text-sm font-medium transition ${isDark ? "bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50" : "bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"}`}
                >
                  {loadingMoreRequests ? t('admin.roleManagement.button.loading', 'Loading...') : t('admin.roleManagement.button.loadMore', 'Load More')}
                </button>
              </div>
            </div>
          </Card>
        </div>
      </div>

      <FeedbackModal
        isOpen={feedback.isOpen}
        onClose={() => setFeedback((prev) => ({ ...prev, isOpen: false }))}
        type={feedback.type}
        title={feedback.title}
        message={feedback.message}
      />
    </>
  );
}
